import createSelector from 'web-app/util/custom-create-selector';
import {feedItemIsObservation, getValueForRichTextBody} from 'web-app/react/entities/feed/items/helpers';
import {observationToFormData} from 'web-app/react/entities/observations/helpers';
import ObservationBehaviors from 'web-app/behaviors/observation-behavior-ids';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import * as NewPostHelpers from 'web-app/react/feed/post/new-post/helpers';
import {type RootState} from 'web-app/react/redux/root-state';

import {type DraftManagerModalState} from './reducer';
import {type ObservationVariant as ObservationVariantType} from './actions';

type SelectorProps = {
    canUseRichTextInPosts?: boolean;
};

const getDraftManagerModalState = (state: RootState): DraftManagerModalState => state.modals.draftManager;

const feedItemSelector = createSelector(
    getDraftManagerModalState,
    (draftManagerState: DraftManagerModalState) => draftManagerState.feedItem,
);

const newPostRecipientsSelector = createSelector(
    getDraftManagerModalState,
    (draftManagerState: DraftManagerModalState) => draftManagerState.newPostRecipients,
);

const selectedRecipientsFromStateSelector = createSelector(
    getDraftManagerModalState,
    (draftManagerState: DraftManagerModalState) => draftManagerState.selectedRecipients,
);

const initialValuesFromStateSelector = createSelector(
    getDraftManagerModalState,
    (draftManagerState: DraftManagerModalState) => draftManagerState.initialValues,
);

const observationVariantFromStateSelector = createSelector(
    getDraftManagerModalState,
    (draftManagerState: DraftManagerModalState) => draftManagerState.observationVariant,
);

export const isObservation = createSelector(
    feedItemSelector,
    (feedItem: any): boolean => feedItem && feedItemIsObservation(feedItem),
);

export const postId = createSelector(
    feedItemSelector,
    isObservation,
    (feedItem: any, isObs: boolean): string | undefined => {
        if (!feedItem) {
            return undefined;
        }

        if (isObs) {
            return feedItem.embed.get('observationId');
        }
        // Ensure 'Post:' is a string before calling replace
        const originatorId = feedItem.originatorId;
        if (typeof originatorId === 'string' && originatorId.startsWith('Post:')) {
            return originatorId.replace('Post:', '');
        }
        return originatorId;
    },
);

export const draftManagerIsEditting = createSelector(postId, (id?: string): boolean => hasValue(id));

export const selectedRecipients = createSelector(
    newPostRecipientsSelector,
    feedItemSelector,
    selectedRecipientsFromStateSelector,
    (newPostRecipients?: string, feedItem?: any, selectedRecipientsFromState?: string): string | undefined => {
        if (selectedRecipientsFromState) {
            return selectedRecipientsFromState;
        }

        if (!feedItem) {
            return newPostRecipients;
        }

        const observation = feedItem.embed.get('observation');
        if (!observation) {
            return undefined;
        }

        return newPostRecipients || observation.children.map(({id}: {id: string}) => id).join(',');
    },
);

export const initialValues = createSelector(
    feedItemSelector,
    initialValuesFromStateSelector,
    isObservation,
    (state: RootState, props: SelectorProps) => props.canUseRichTextInPosts,
    (feedItem: any, customInitialValues: any, isObs: boolean, canUseRichTextInPosts?: boolean): any | undefined => {
        if (!feedItem) {
            if (customInitialValues) {
                return customInitialValues;
            }
            return undefined;
        }

        if (isObs) {
            const observation = feedItem.embed.get('observation');
            if (!observation) {
                return undefined;
            }
            return observationToFormData(observation, true);
        }

        const richTextBody = getValueForRichTextBody(feedItem);
        return {
            body: canUseRichTextInPosts ? undefined : feedItem.body,
            richTextBody: canUseRichTextInPosts ? richTextBody : undefined,
            disableFeedback: feedItem.disableFeedback,
            activities: NewPostHelpers.embedToActivityFormValues(feedItem.embed),
            selectedRecipients: feedItem.receivers.join(','),
        };
    },
);

export const observationVariant = createSelector(
    observationVariantFromStateSelector,
    isObservation,
    feedItemSelector,
    (variantFromState: ObservationVariantType, isObs: boolean, feedItem: any): ObservationVariantType | undefined => {
        if (isObs && feedItem) {
            const observation = feedItem.embed.get('observation');
            return observation.variant;
        }
        return variantFromState;
    },
);

export const behaviors = createSelector(
    isObservation,
    feedItemSelector,
    (isObs: boolean, feedItem: any): any | null => {
        if (isObs && feedItem) {
            const observation = feedItem.embed.get('observation');
            return observation.behaviors;
        }
        return null;
    },
);

export const canSendObservation = createSelector(behaviors, (behavior: any): boolean => {
    return behavior && behavior.includes(ObservationBehaviors.CanEditObservation);
});

export const pollId = createSelector(feedItemSelector, (feedItem: any): string | undefined => {
    if (!feedItem) {
        return undefined;
    }
    return feedItem.get('pollId');
});
